// src/engine/dataset/DatasetDataProvider.jsx
import React, {
  createContext,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  useContext,
} from "react";
import { DatasetEngine } from "./DatasetEngine.js";
import { buildRefreshSpecFromBaseService } from "./EngineRefreshAdapter.js";

const DatasetDataContext = createContext(null);

// Bundler-safe worker URL
const WORKER_URL = new URL("./workers/DatasetEngineWorker.js", import.meta.url).toString();

// Token we swap to the real table name before sending to the engine
const TABLE_TOKEN = "<|TableName|>";

function defaultTableNameFor(id) {
  return `ds_${String(id ?? "").replace(/-/g, "")}`;
}

/** Replace any known base/warehouse table occurrences with the token */
function normalizeSqlToEngineTable(rawSql, datasetLike, tableToken = TABLE_TOKEN) {
  let s = String(rawSql || "");
  const baseName =
    datasetLike?.QueryTemplateTableName ||
    datasetLike?.tableName ||
    datasetLike?.Name ||
    datasetLike?._id;

  if (baseName) {
    const safe = String(baseName).replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
    const re = new RegExp(`\\b${safe}\\b`, "gi");
    s = s.replace(re, tableToken);
  }
  return s;
}

/** Expand <|TableName|> to the actual engine table */
function expandTable(sql, tableName) {
  return String(sql ?? "").replaceAll(TABLE_TOKEN, tableName);
}

// --- rowCount helpers -------------------------------------------------------
function toSafeInt(v) {
  if (typeof v === 'bigint') {
    try {
      const MIN_SAFE = BigInt(Number.MIN_SAFE_INTEGER);
      const MAX_SAFE = BigInt(Number.MAX_SAFE_INTEGER);
      if (v >= MIN_SAFE && v <= MAX_SAFE) return Number(v);
    } catch {}
    // out-of-range BigInt → string then int (best-effort)
    const n = Number.parseInt(v.toString(), 10);
    return Number.isFinite(n) ? n : null;
  }
  const n = Number(v);
  if (!Number.isFinite(n)) return null;
  return Math.trunc(n);
}

async function fetchRowCountSafe(engine, datasetId, tableName) {
  // 1) prefer DOUBLE to avoid bigint plumbing weirdness
  try {
    const r1 = await engine.query(datasetId, `SELECT CAST(COUNT(*) AS DOUBLE) AS c FROM ${tableName}`);
    const n1 = toSafeInt(r1?.[0]?.c);
    if (n1 != null) return n1;
  } catch {}
  // 2) try raw COUNT(*)
  try {
    const r2 = await engine.query(datasetId, `SELECT COUNT(*) AS c FROM ${tableName}`);
    const n2 = toSafeInt(r2?.[0]?.c);
    if (n2 != null) return n2;
  } catch {}
  // 3) final fallback: dataset_meta row_count (set by the worker on refresh)
  try {
    const q = `SELECT row_count AS c FROM dataset_meta WHERE dataset_id = ${JSON.stringify(String(datasetId))} LIMIT 1`;
    const r3 = await engine.query(datasetId, q);
    const n3 = toSafeInt(r3?.[0]?.c);
    if (n3 != null) return n3;
  } catch {}
  return null;
}

export function DatasetDataProvider({
  children,
  // Inputs used to build the refresh spec
  baseItemsUri,
  appName,
  apiName = "DashboardDataset",
  operationType = "REFRESH_DATASET_DATA",
  processorType,
  defaultRefreshIntervalMs = null,
  autoInit = true,
}) {
  const engineRef = useRef(null);
  const workerRef = useRef(null);
  const initInFlightRef = useRef(null); // ← prevents double init

  // ---- Engine state ----
  const [engineReady, setEngineReady] = useState(false);
  const [engineError, setEngineError] = useState(null);
  const [eventStream, setEventStream] = useState([]);

  // ---- Dataset registry & status ----
  const [datasetsById, setDatasetsById] = useState({});
  const [statusByDatasetId, setStatusByDatasetId] = useState({});

  // ---- Refresh timers ----
  const refreshTimersRef = useRef({});

  // Cache config for spec-building
  const configRef = useRef({
    baseItemsUri,
    appName,
    apiName,
    operationType,
    processorType,
  });
  useEffect(() => {
    configRef.current = { baseItemsUri, appName, apiName, operationType, processorType };
  }, [baseItemsUri, appName, apiName, operationType, processorType]);

  // -------- Engine helpers --------
  const getEngineDiagnostics = useCallback(async () => {
    return {
      ready: !!engineRef.current,
      error: engineError || null,
      workerUrl: WORKER_URL,
      initInFlight: !!initInFlightRef.current,
    };
  }, [engineError]);

  const initEngine = useCallback(async (opts = {}) => {
    // Already created?
    if (engineRef.current) return engineRef.current;
    // In flight? return the same promise
    if (initInFlightRef.current) return initInFlightRef.current;

    initInFlightRef.current = (async () => {
      try {
        setEngineError(null);

        // Create a single Worker instance and keep it
        const worker = new Worker(WORKER_URL, {
          name: "DatasetEngineWorker",
          // If your build outputs an ES module worker, uncomment:
          // type: "module",
        });
        workerRef.current = worker;

        // Stream worker EVENTS up to UI
        const onMsg = (e) => {
          if (e?.data?.type === "EVENT") {
            setEventStream((prev) => [...prev.slice(-199), e.data.event]);
          }
        };
        worker.addEventListener("message", onMsg);

        // Create engine and INIT (file-backed by default)
        const engine =
          (DatasetEngine && typeof DatasetEngine.create === "function")
            ? await DatasetEngine.create({
                worker,
                config: {
                  // Matches your worker cfg (dbName/useOPFS)
                  dbName: "dataset-engine.duckdb",
                  useOPFS: true,
                },
                trace: false,
              })
            : (typeof DatasetEngine === "function"
                ? new DatasetEngine({ worker })
                : null);

        if (!engine) throw new Error("Engine instance was not created (null/undefined).");

        engineRef.current = engine;
        setEngineReady(true);
        return engine;
      } catch (e) {
        setEngineError(e?.message || String(e));
        setEngineReady(false);
        engineRef.current = null;
        // Ensure worker is torn down if init failed
        try { workerRef.current?.terminate?.(); } catch {}
        workerRef.current = null;
        throw e;
      } finally {
        // allow future re-init only after this attempt finishes
        initInFlightRef.current = null;
      }
    })();

    return initInFlightRef.current;
  }, []);

  // Idempotent auto-init (StrictMode-safe)
  useEffect(() => {
    if (!autoInit) return;
    void initEngine();
  }, [autoInit, initEngine]);

  // -------- Dataset helpers --------
  const getTableName = useCallback(
    (datasetId) => (datasetsById[datasetId]?.tableName || defaultTableNameFor(datasetId)),
    [datasetsById]
  );

  const setStatus = useCallback((datasetId, state, error = null) => {
    setStatusByDatasetId((prev) => ({
      ...prev,
      [datasetId]: { state, ...(error ? { error } : { error: null }) },
    }));
  }, []);

  // Build the refresh spec **from provider config**, not from the dataset blob
  const buildSpecForDataset = useCallback(
    async (datasetId, overrides = {}) => {
      const cfg = configRef.current;
      return buildRefreshSpecFromBaseService({
        baseItemsUri: overrides.baseItemsUri ?? cfg.baseItemsUri,
        appName: overrides.appName ?? cfg.appName,
        apiName: overrides.apiName ?? cfg.apiName,
        operationType: overrides.operationType ?? cfg.operationType,
        id: datasetId,
        item: overrides.item ?? null,
        queryParams: overrides.queryParams ?? null,
        processorType: overrides.processorType ?? cfg.processorType ?? null,
      });
    },
    []
  );

  // --- Define refreshDataset FIRST (so it's initialized before any use) ---
  const refreshDataset = useCallback(
    async (datasetId, overrides = {}) => {
      if (!engineRef.current) throw new Error("Engine not ready");
      const ds = datasetsById[datasetId]?.meta;
      if (!ds) throw new Error(`Unknown dataset: ${datasetId}`);

      setStatus(datasetId, "loading");

      try {
        const spec = await buildSpecForDataset(datasetId, overrides);
        const tableName = getTableName(datasetId);
        let rowCount = null;

        // If engine exposes a dedicated refresh, use it
        if (typeof engineRef.current.refreshDataset === "function") {
          await engineRef.current.refreshDataset(datasetId, spec);
        } else {
          // Fallback: persist via SQL result using LAST statement
          if (spec?.sql) {
            const sqls = Array.isArray(spec.sql) ? spec.sql : [spec.sql];
            const last = sqls[sqls.length - 1];
            const normalized = normalizeSqlToEngineTable(last, ds);
            const expanded = expandTable(normalized, tableName);
            const persisted = `CREATE OR REPLACE TABLE ${tableName} AS ${expanded}`;
            await engineRef.current.query(datasetId, persisted);
          } else if (spec?.tableName && Array.isArray(spec?.rows)) {
            if (engineRef.current.upsertTableFromRows) {
              await engineRef.current.upsertTableFromRows(spec.tableName || tableName, spec.rows);
            } else if (engineRef.current.loadRows) {
              await engineRef.current.loadRows(spec.tableName || tableName, spec.rows);
            } else {
              throw new Error(
                "Engine cannot ingest 'rows': missing upsertTableFromRows/loadRows API."
              );
            }
          }
        }

        // Best-effort row count
        try {
          const rc = await engineRef.current.query(
            datasetId,
            `SELECT COUNT(*) AS c FROM ${tableName}`
          );
          rowCount = Array.isArray(rc) && rc[0] ? Number(rc[0].c) : null;
        } catch { /* ignore */ }

        setDatasetsById((prev) => {
          const prior = prev[datasetId] ?? {};
          return {
            ...prev,
            [datasetId]: {
              ...prior,
              lastRefreshAt: new Date().toISOString(),
              refreshCount: (prior.refreshCount ?? 0) + 1,
              rowCount: rowCount ?? prior.rowCount ?? null,
            },
          };
        });

        setStatus(datasetId, "ready");
      } catch (e) {
        console.error("[DatasetDataProvider] refreshDataset failed:", e);
        setStatus(datasetId, "error", e?.message || String(e));
        throw e;
      }
    },
    [datasetsById, buildSpecForDataset, getTableName, setStatus]
  );

  // --- Now define addDataset (safe to reference refreshDataset) ---
  const addDataset = useCallback(
    async (dataset, opts = {}) => {
      if (!engineRef.current) throw new Error("Engine not ready");

      const id = dataset?._id || dataset?.Id;
      if (!id) throw new Error("Dataset missing _id/Id.");

      const refreshOnAdd = Object.prototype.hasOwnProperty.call(opts, "refreshOnAdd")
        ? !!opts.refreshOnAdd
        : (Object.prototype.hasOwnProperty.call(opts, "withRefresh") ? !!opts.withRefresh : true);

      // Local bookkeeping (default table name)
      setDatasetsById((prev) => {
        const prior = prev[id] ?? {};
        return {
          ...prev,
          [id]: {
            meta: dataset,
            tableName: prior.tableName ?? defaultTableNameFor(id),
            refreshPlanMs: prior.refreshPlanMs ?? null,
            lastRefreshAt: prior.lastRefreshAt ?? null,
            refreshCount: prior.refreshCount ?? 0,
            rowCount: prior.rowCount ?? null,
          },
        };
      });

      setStatus(id, "loading");

      try {
        // Build refresh spec from provider config (NOT from dataset blob)
        const refreshSpec = refreshOnAdd ? await buildSpecForDataset(id, opts) : null;

        // Let the worker register AND (optionally) refresh; it awaits refresh internally
        if (typeof engineRef.current.addDataset === "function") {
          await engineRef.current.addDataset(
            { ...dataset, tableName: defaultTableNameFor(id) },
            { refreshOnAdd, refreshSpec }
          );
        }

        // Best-effort row count after add/refresh completes
        let rowCount = null;
        const tableName = getTableName(id);
        try {
          const rc = await engineRef.current.query(id, `SELECT COUNT(*) AS c FROM ${tableName}`);
          rowCount = Array.isArray(rc) && rc[0] ? Number(rc[0].c) : null;
        } catch { /* ignore */ }

        setDatasetsById((prev) => {
          const prior = prev[id] ?? {};
          return {
            ...prev,
            [id]: {
              ...prior,
              lastRefreshAt: refreshOnAdd ? new Date().toISOString() : prior.lastRefreshAt,
              refreshCount: refreshOnAdd ? (prior.refreshCount ?? 0) + 1 : prior.refreshCount ?? 0,
              rowCount: rowCount ?? prior.rowCount ?? null,
            },
          };
        });

        // Optional default refresh plan
        if (defaultRefreshIntervalMs && defaultRefreshIntervalMs > 0) {
          try {
            const h = setInterval(() => {
              refreshDataset(id).catch(() => {});
            }, defaultRefreshIntervalMs);
            refreshTimersRef.current[id] = h;
            setDatasetsById((prev) => ({
              ...prev,
              [id]: { ...(prev[id] ?? {}), refreshPlanMs: defaultRefreshIntervalMs },
            }));
          } catch { /* ignore */ }
        }

        setStatus(id, refreshOnAdd ? "ready" : "idle");
      } catch (e) {
        console.error("[DatasetDataProvider] addDataset failed:", e);
        setStatus(id, "error", e?.message || String(e));
        throw e;
      }
    },
    [buildSpecForDataset, getTableName, refreshDataset, setStatus, defaultRefreshIntervalMs]
  );

  const setRefreshPlan = useCallback(
    async (datasetId, intervalMsOrNull) => {
      const current = refreshTimersRef.current[datasetId];
      if (current) {
        clearInterval(current);
        delete refreshTimersRef.current[datasetId];
      }

      setDatasetsById((prev) => ({
        ...prev,
        [datasetId]: {
          ...(prev[datasetId] ?? {}),
          refreshPlanMs: intervalMsOrNull ?? null,
        },
      }));

      if (intervalMsOrNull && intervalMsOrNull > 0) {
        const handle = setInterval(() => {
          refreshDataset(datasetId).catch(() => {});
        }, intervalMsOrNull);
        refreshTimersRef.current[datasetId] = handle;
      }
    },
    [refreshDataset]
  );

  const queryDataset = useCallback(
    async (datasetId, sql, params) => {
      if (!engineRef.current) throw new Error("Engine not ready");
      const tableName = getTableName(datasetId);
      const expanded = String(sql ?? "").replaceAll(TABLE_TOKEN, tableName);
      if (!expanded.trim()) throw new Error("SQL is empty");

      try {
        const rows = await engineRef.current.query(datasetId, expanded, params);
        if (Array.isArray(rows)) return rows;
        if (rows && Array.isArray(rows.rows)) return rows.rows;
        return rows ?? [];
      } catch (e) {
        const base = e?.message || "Engine query failed";
        const details = e?.info ? ` Details: ${JSON.stringify(e.info)}` : "";
        throw new Error(base + details);
      }
    },
    [getTableName]
  );

  const listDatasets = useCallback(async () => {
    const summaries = Object.entries(datasetsById).map(([id, ds]) => {
      const st = statusByDatasetId[id] ?? { state: "idle", error: null };
      return {
        id,
        name:
          ds?.meta?.Name ??
          ds?.meta?.DisplayName ??
          ds?.meta?.Title ??
          ds?.meta?.DatasetName ??
          id,
        tableName: ds?.tableName ?? defaultTableNameFor(id),
        state: st.state ?? "idle",
        refreshPlanMs: ds?.refreshPlanMs ?? null,
        lastRefreshAt: ds?.lastRefreshAt ?? null,
        refreshCount: ds?.refreshCount ?? 0,
        rowCount: typeof ds?.rowCount === "number" ? ds.rowCount : null,
        error: st.error ?? null,
      };
    });
    return summaries;
  }, [datasetsById, statusByDatasetId]);

  /**
   * Build a sharable debug bundle (markdown text + raw payload).
   * Call this from any UI: const { text, payload } = await buildDebugBundle({...});
   */
  const buildDebugBundle = useCallback(
    async (opts = {}) => {
      const {
        selectedDatasetId = null,
        queryText = "",
        sampleRows = [],
        totalRowsLastRun = Array.isArray(sampleRows) ? sampleRows.length : 0,
        ui = {},
      } = opts;

      const env = {
        userAgent: typeof navigator !== "undefined" ? navigator.userAgent : null,
        time: new Date().toISOString(),
      };

      const engineDatasets = await listDatasets().catch(() => []);
      const payload = {
        prompt:
          "Analyze and debug issues in my browser-based DuckDB dataset engine. Use the JSON sections below.",
        env,
        engine: {
          ready: !!engineRef.current,
          error: engineError || null,
          selectedDatasetId,
          status: selectedDatasetId ? statusByDatasetId[selectedDatasetId] : null,
          intervalMs:
            (selectedDatasetId &&
              datasetsById[selectedDatasetId] &&
              datasetsById[selectedDatasetId].refreshPlanMs) ||
            null,
          engineDatasets,
        },
        dataset: {
          loadedMetadata:
            (selectedDatasetId && datasetsById[selectedDatasetId]?.meta) || null,
        },
        query: {
          text: queryText,
          sampleRows: Array.isArray(sampleRows) ? sampleRows.slice(0, 5) : [],
          sampleRowCount: Array.isArray(sampleRows) ? Math.min(sampleRows.length, 5) : 0,
          totalRowsLastRun,
        },
        ui,
      };

      const text =
        `### Engine Debug Bundle\n` +
        "```json\n" +
        JSON.stringify(payload, null, 2) +
        "\n```\n(Please paste this bundle into the chat.)";

      return { payload, text };
    },
    [engineError, datasetsById, statusByDatasetId, listDatasets]
  );

  // ========= NEW: Engine reinit & utilities =========
  const getEngineInfo = useCallback(async () => {
    if (!engineRef.current) await initEngine();
    return await engineRef.current.getEngineInfo();
  }, [initEngine]);

  const flushFiles = useCallback(async () => {
    if (!engineRef.current) await initEngine();
    return await engineRef.current.flushFiles();
  }, [initEngine]);

  /**
   * Reinitialize worker-side DuckDB (memory or file) and optionally re-add datasets.
   *
   * options:
   *  - mode: "memory" | "file"
   *  - dbName?: string
   *  - useOPFS?: boolean
   *  - requireFile?: boolean
   *  - dropOldFile?: boolean = true
   *  - reAdd?: boolean = false              // re-register previously known datasets
   *  - refreshOnAdd?: boolean = true        // when re-adding
   *  - timeoutMs?: number
   */
  const reinitEngineDb = useCallback(
    async (options = {}) => {
      if (!engineRef.current) await initEngine();

      // Stop provider-side timers immediately
      const timers = refreshTimersRef.current;
      Object.keys(timers).forEach((k) => clearInterval(timers[k]));
      refreshTimersRef.current = {};

      // Snapshot current registry if we might re-add
      const snapshot = options?.reAdd ? { ...datasetsById } : null;

      // Show transient "loading" state
      setEngineReady(false);
      setEngineError(null);

      try {
        // Ask the engine to reinitialize its DuckDB
        await engineRef.current.reinitDb(options);

        if (options?.reAdd && snapshot) {
          // Re-add each dataset (optionally refreshing on add)
          const ids = Object.keys(snapshot);
          // Reset local state first so we don't carry stale row counts
          setDatasetsById({});
          setStatusByDatasetId({});
          for (const id of ids) {
            const dsMeta = snapshot[id]?.meta;
            if (!dsMeta) continue;

            // mark loading
            setStatusByDatasetId((prev) => ({ ...prev, [id]: { state: "loading", error: null } }));

            try {
              await addDataset(dsMeta, { refreshOnAdd: options?.refreshOnAdd !== false });
              // refresh timers if default interval configured
              if (defaultRefreshIntervalMs && defaultRefreshIntervalMs > 0) {
                const h = setInterval(() => {
                  refreshDataset(id).catch(() => {});
                }, defaultRefreshIntervalMs);
                refreshTimersRef.current[id] = h;
              }
            } catch (e) {
              // keep error per-dataset
              setStatusByDatasetId((prev) => ({ ...prev, [id]: { state: "error", error: e?.message || String(e) } }));
            }
          }
        } else {
          // Not re-adding: clear local registry & status
          setDatasetsById({});
          setStatusByDatasetId({});
        }

        setEngineReady(true);
        return await engineRef.current.getEngineInfo();
      } catch (e) {
        setEngineError(e?.message || String(e));
        setEngineReady(false);
        throw e;
      }
    },
    [addDataset, datasetsById, defaultRefreshIntervalMs, initEngine, refreshDataset]
  );

  // ========= NEW: bridge engine pub/sub -> context =========
  const subscribeDataset = useCallback((datasetId, fn) => {
    // If engine isn’t ready yet or API missing, provide a safe no-op unsubscriber.
    const engine = engineRef.current;
    if (!engine || typeof engine.subscribeDataset !== "function") return () => {};
    return engine.subscribeDataset(datasetId, fn);
  }, []);

  // Clean up timers and engine on unmount (avoid stale workers/listeners)
  useEffect(() => {
    return () => {
      const timers = refreshTimersRef.current;
      Object.keys(timers).forEach((k) => clearInterval(timers[k]));
      refreshTimersRef.current = {};
      try { engineRef.current?.stopAll?.(); } catch {}
      try { workerRef.current?.terminate?.(); } catch {}
      engineRef.current = null;
      workerRef.current = null;
      setEngineReady(false);
    };
  }, []);

  // -------- Provider value --------
  const value = useMemo(
    () => ({
      // engine
      engineReady,
      engineError,
      eventStream,
      initEngine,
      getEngineDiagnostics,
      // NEW: engine ops
      reinitEngineDb,
      getEngineInfo,
      flushFiles,
      // dataset ops
      addDataset,
      refreshDataset,
      setRefreshPlan,
      listDatasets,
      queryDataset,
      getTableName,
      statusByDatasetId,
      // utils
      buildDebugBundle,
      // expose spec builder if you need it elsewhere
      buildSpecForDataset,
      // NEW: live subscription (used by useDataset / charts)
      subscribeDataset,
    }),
    [
      engineReady,
      engineError,
      eventStream,
      initEngine,
      getEngineDiagnostics,
      reinitEngineDb,
      getEngineInfo,
      flushFiles,
      addDataset,
      refreshDataset,
      setRefreshPlan,
      listDatasets,
      queryDataset,
      getTableName,
      statusByDatasetId,
      buildDebugBundle,
      buildSpecForDataset,
      subscribeDataset,
    ]
  );

  return (
    <DatasetDataContext.Provider value={value}>
      {children}
    </DatasetDataContext.Provider>
  );
}

export function useDatasetData() {
  const ctx = useContext(DatasetDataContext);
  if (!ctx) throw new Error("useDatasetData must be used within a DatasetDataProvider");
  return ctx;
}
